import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { openURLInNewTab } from "utils/common";
import { BOX_MAX_WIDTH } from "utils/constants";
import { reverse } from "utils/urls";

import Box from "components/Box";
import Button from "components/Button";
import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Select from "components/Select";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";
import { usePageTitle } from "components/usePageTitle";

import InfoBanner from "entities/AISummaries/components/InfoBanner";
import PromptsAndAnswers from "entities/AISummaries/components/PromptsAndAnswers";
import {
  openAIDocumentAssistantDocumentTypeUpdate,
  useOpenAIDocumentAssistant,
  useOpenAIDocumentTypes,
  usePromptList,
} from "entities/AISummaries/sdk";

const AISummariesEditor = () => {
  const navigate = useNavigate();
  usePageTitle("Builders Patch: AI Summaries - questions and answers");
  const [hasOpenaAIAnswerInProcess, setHasOpenaAIAnswerInProcess] =
    useState(false);

  const params = useParams<{
    dealId: string;
    openAIDocumentAssistantId: string;
  }>();
  const dealId = Number(params.dealId);
  const openAIDocumentAssistantId = Number(params.openAIDocumentAssistantId);

  const { data: documentTypes, isLoading: documentTypesAreLoading } =
    useOpenAIDocumentTypes();

  const {
    data: documentAssistant,
    isLoading: documentAssistantIsLoading,
    mutate: mutateDocumentAssistant,
    error,
  } = useOpenAIDocumentAssistant({
    openAIDocumentAssistantId,
  });

  const { data: prompts, mutate: mutatePrompts } = usePromptList({
    openAIDocumentAssistantId,
  });

  const { show: showConfirmationDialog } = useConfirmationDialog();

  useEffect(() => {
    if (error?.detail === "Not found.") {
      navigate(URLS.NOT_FOUND, { replace: true });
    }
  }, [error, navigate]);

  useEffect(() => {
    if (!_.isNil(prompts)) {
      setHasOpenaAIAnswerInProcess(
        prompts.some((prompt) =>
          prompt.prompt_responses.some((response) =>
            _.isNil(response.readable_answer)
          )
        )
      );
    }
  }, [prompts]);

  const handleDownloadFile = useCallback(
    ({ fileUrl }: { fileUrl: string | undefined }) => {
      if (!_.isNil(fileUrl)) {
        openURLInNewTab({ url: fileUrl });
      }
    },
    []
  );

  const changeDocumentType = useCallback(
    ({ newDocumentTypeId }: { newDocumentTypeId: number }) =>
      openAIDocumentAssistantDocumentTypeUpdate({
        openAIDocumentAssistantId,
        openAIDocumentTypeId: newDocumentTypeId,
      })
        .then(() => {
          toast.successMessage(
            "The document type has been successfully updated!"
          );
          mutateDocumentAssistant();
          mutatePrompts();
        })
        .catch((errors) => {
          const errorMessage = errors?.message || errors?.detail;

          if (!_.isNil(errorMessage)) {
            toast.errorMessage(errorMessage);
          } else {
            errors?.forEach?.((error: unknown) => {
              if (_.isString(error)) {
                toast.errorMessage(error);
              }
            });
          }
        }),
    [openAIDocumentAssistantId, mutateDocumentAssistant, mutatePrompts]
  );

  const handleDocumentTypeChange = useCallback(
    (event: React.ChangeEvent<{ value: string }>) => {
      const newDocumentTypeId = Number(event.target.value);

      if (_.isNil(documentAssistant?.openai_document_type)) {
        changeDocumentType({ newDocumentTypeId });
      } else {
        showConfirmationDialog({
          onConfirm: () => changeDocumentType({ newDocumentTypeId }),
          confirmButtonText: "Yes, modify it",
          message: (
            <>
              <Text variant="h2" marginBottom={1}>
                Modifying document type
              </Text>
              <Text variant="text2">
                Changing the document type will delete all previously generated
                answers under the current document type. Are you sure you want
                to proceed?
              </Text>
            </>
          ),
        });
      }
    },
    [documentAssistant, changeDocumentType, showConfirmationDialog]
  );

  const showInfoBanner = useMemo(
    () => _.isNil(documentAssistant?.openai_document_type),
    [documentAssistant]
  );

  const reasonForButtonBeingDisabled = useMemo(() => {
    if (hasOpenaAIAnswerInProcess) {
      return "Please wait for the current answer to be generated.";
    }

    return "";
  }, [hasOpenaAIAnswerInProcess]);

  return (
    <Paper
      sx={{
        overflowX: "auto",
        padding: theme.spacing(3, 4),
      }}
    >
      <Stack>
        <Box
          sx={{ maxWidth: BOX_MAX_WIDTH, alignSelf: "center", width: "100%" }}
        >
          <Stack spacing={4}>
            <PageHeader
              backLink={reverse(URLS.DEAL_AI_SUMMARIES, {
                dealId,
                openAIDocumentAssistantId,
              })}
              backTitle="AI Summaries"
              title={
                documentAssistantIsLoading ? (
                  <Skeleton height="34px" />
                ) : (
                  <Text variant="h2" data-testid="page-header-name">
                    {documentAssistant?.file?.original_file_name}
                  </Text>
                )
              }
            />
            <Stack spacing={5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                spacing={1}
              >
                {documentAssistantIsLoading || documentTypesAreLoading ? (
                  <>
                    <Skeleton width="50%" height="56px" />
                    <Skeleton variant="text" width="150px" height="24px" />
                  </>
                ) : (
                  <Stack spacing={3} width="100%">
                    {showInfoBanner && <InfoBanner />}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-end"
                    >
                      <Tooltip title={reasonForButtonBeingDisabled}>
                        <span style={{ flexBasis: "50%" }}>
                          <Select
                            fullWidth
                            options={
                              documentTypes?.map((documentType) => ({
                                label: documentType.name,
                                value: documentType.id.toString(),
                              })) || []
                            }
                            value={documentAssistant?.openai_document_type?.toString()}
                            onChange={handleDocumentTypeChange}
                            label="Document Type"
                            disabled={hasOpenaAIAnswerInProcess}
                          />
                        </span>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() =>
                          handleDownloadFile({
                            fileUrl: documentAssistant?.file.url,
                          })
                        }
                      >
                        View original document
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
              {!_.isNil(documentAssistant?.openai_document_type) && (
                <PromptsAndAnswers
                  hasOpenaAIAnswerInProcess={hasOpenaAIAnswerInProcess}
                  onGenerateAnswer={() => setHasOpenaAIAnswerInProcess(true)}
                />
              )}
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export default AISummariesEditor;
